import { ReactNode } from 'react';

interface SkeletonLoaderProps {
  type?: 'text' | 'title' | 'avatar' | 'thumbnail' | 'button' | 'custom';
  width?: string;
  height?: string;
  className?: string;
  count?: number;
  children?: ReactNode;
}

export function SkeletonLoader({
  type = 'text',
  width,
  height,
  className = '',
  count = 1,
  children
}: SkeletonLoaderProps) {
  const getTypeClasses = () => {
    switch (type) {
      case 'text':
        return 'h-4 w-full rounded';
      case 'title':
        return 'h-6 w-3/4 rounded';
      case 'avatar':
        return 'h-10 w-10 rounded-full';
      case 'thumbnail':
        return 'h-20 w-20 rounded-lg';
      case 'button':
        return 'h-10 w-24 rounded-lg';
      case 'custom':
        return '';
      default:
        return 'h-4 w-full rounded';
    }
  };

  const skeletonElement = (
    <div
      className={`
        animate-pulse bg-gray-200
        ${getTypeClasses()}
        ${className}
      `}
      style={{
        width: width || undefined,
        height: height || undefined
      }}
    >
      {children}
    </div>
  );

  if (count > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index}>{skeletonElement}</div>
        ))}
      </div>
    );
  }

  return skeletonElement;
}

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 5,
  showHeader = true,
  className = ''
}: TableSkeletonProps) {
  return (
    <div className={`w-full ${className}`}>
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full">
          {showHeader && (
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: columns }).map((_, index) => (
                  <th key={index} className="px-4 py-3">
                    <SkeletonLoader type="text" className="h-4 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <td key={colIndex} className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      {colIndex === 0 && (
                        <SkeletonLoader type="avatar" className="flex-shrink-0" />
                      )}
                      <div className="flex-1 space-y-2">
                        <SkeletonLoader type="text" className="h-4 w-3/4" />
                        {colIndex === 0 && (
                          <SkeletonLoader type="text" className="h-3 w-1/2" />
                        )}
                      </div>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface MetricCardSkeletonProps {
  count?: number;
  className?: string;
}

export function MetricCardSkeleton({ count = 4, className = '' }: MetricCardSkeletonProps) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 animate-pulse">
          <div className="flex items-start justify-between mb-3">
            <div className="h-9 w-9 rounded-lg bg-gray-200" />
            <div className="h-4 w-16 rounded bg-gray-200" />
          </div>
          <div className="space-y-2">
            <div className="h-7 w-20 rounded bg-gray-200" />
            <div className="h-4 w-28 rounded bg-gray-200" />
            <div className="h-3 w-16 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface CardSkeletonProps {
  showImage?: boolean;
  lines?: number;
  className?: string;
}

export function CardSkeleton({
  showImage = false,
  lines = 3,
  className = ''
}: CardSkeletonProps) {
  return (
    <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
      {showImage && (
        <SkeletonLoader type="custom" className="h-48 w-full rounded-lg mb-4" />
      )}
      <SkeletonLoader type="title" className="mb-4" />
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <SkeletonLoader
            key={index}
            type="text"
            className={index === lines - 1 ? 'w-2/3' : ''}
          />
        ))}
      </div>
      <div className="mt-4 flex space-x-2">
        <SkeletonLoader type="button" />
        <SkeletonLoader type="button" />
      </div>
    </div>
  );
}